import { useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { CompanyClubContext } from '@contexts/company-club/company-club-context';
import { NavigationContext } from '@contexts/navigation/navigation-context';

import type { CompanyClubRoute } from './use-open-company-club-route.type';

/**
 * Opens a route that may belong to another company or venue than the one on screen. Inside the current pair it is a
 * plain navigation. Anything else reloads after navigating, like `SelectCompany`: the company and venue context, the
 * auth context and the profiler are then built again from the URL, instead of the previous pair's state lingering.
 */
export const useOpenCompanyClubRoute = () => {
  const navigate = useNavigate();
  const { companyState, clubState } = useContext(CompanyClubContext);
  const { preventRedirect, setIsItemClicked, setRedirectUrl } = useContext(NavigationContext);

  return useCallback(
    ({ companyLegacyId, clubSlug, path }: CompanyClubRoute) => {
      // Unsaved changes: the form on screen asks first and navigates to the url itself once they are discarded.
      if (preventRedirect) {
        setIsItemClicked(true);
        setRedirectUrl(path);
        return;
      }

      navigate(path);
      if (companyState.legacyId !== companyLegacyId || clubState.slug !== clubSlug) navigate(0);
    },
    [navigate, companyState.legacyId, clubState.slug, preventRedirect, setIsItemClicked, setRedirectUrl]
  );
};
